// The state of a Dashboard card that reads one or more widget requests.
import dayjs from '~libs/dayjs';

import { DashboardComputedAt } from '~support-at-home/models/dashboard';

/** $muted-gray (styles/modules/_variables.scss), the colour of the loading spinners */
export const MUTED_GREY = '#6c757d';

export type DashboardWidgetPhase = 'loading' | 'error' | 'ready';

export interface DashboardWidgetStatus {
  phase: DashboardWidgetPhase;
  error?: unknown;
  retry: () => void;
}

interface WidgetQuery {
  data?: unknown;
  isFetching: boolean;
  isError: boolean;
  error?: unknown;
  refetch: () => unknown;
}

export const READY_WIDGET: DashboardWidgetStatus = { phase: 'ready', retry: () => undefined };

/**
 * A card's state from the queries it reads: an error when any failed (Retry reloads only those), loading while any is
 * fetching or has no figures yet, otherwise ready.
 */
export const widgetStatus = (...queries: WidgetQuery[]): DashboardWidgetStatus => {
  const failed = queries.filter((query) => query.isError);
  if (failed.length > 0) {
    return {
      phase: 'error',
      error: failed[0].error,
      retry: () => failed.forEach((query) => query.refetch()),
    };
  }
  if (queries.some((query) => query.isFetching || query.data === undefined)) {
    return { phase: 'loading', retry: () => undefined };
  }
  return READY_WIDGET;
};

/** The query as fetching for the whole of a page refresh (TAV-3351), so every card shows its loading state until the last figures are back. */
export const whileRefreshing = <Query extends { isFetching: boolean }>(query: Query, refreshing: boolean): Query =>
  refreshing ? { ...query, isFetching: true } : query;

/** The earliest computed_at of the widgets; null when any has none yet (loading, or an api without the cache). */
export const oldestComputedAt = (...widgets: Array<DashboardComputedAt | undefined>): string | null => {
  const times = widgets.map((widget) => widget?.computed_at);
  if (times.some((time) => !time)) return null;
  return (times as string[]).reduce((oldest, time) => (dayjs(time).isBefore(dayjs(oldest)) ? time : oldest));
};
